import { usePathname } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { type ColorSchemeName } from 'react-native';
import { YStack } from 'tamagui';

import { H5Emphasized, H5Regular } from '../components/text/Typography';
import { activeThemeAtom } from './BlenderAtoms';
import { BlenderComponentMap } from './BlenderDirectory';
import { BlenderLayout } from './BlenderLayout';

type LightOrDarkTheme = Exclude<ColorSchemeName, null | undefined>;

interface BlenderViewProps {
    componentName: string;
    theme: LightOrDarkTheme;
    onToggleTheme: (theme: LightOrDarkTheme) => void;
    currentPath?: string;
}

export const BlenderView = ({
    componentName,
    theme,
    onToggleTheme,
    currentPath: providedPath,
}: BlenderViewProps) => {
    const { component: Component, title } =
        BlenderComponentMap[componentName] ?? {};

    // Use provided pathname (should be passed from app-level component with expo-router context)
    const pathname = providedPath ?? usePathname();

    const setActiveTheme = useSetAtom(activeThemeAtom);

    useEffect(() => {
        setActiveTheme(theme);
    }, [theme]);

    if (!Component) {
        return (
            <BlenderLayout
                theme={theme}
                onToggleTheme={onToggleTheme}
                currentPath={pathname}
            >
                <YStack>
                    <H5Regular>Component not found: {componentName}</H5Regular>
                </YStack>
            </BlenderLayout>
        );
    }

    return (
        <BlenderLayout
            theme={theme}
            onToggleTheme={onToggleTheme}
            currentPath={pathname}
        >
            <H5Emphasized>{title}</H5Emphasized>
            <Component />
        </BlenderLayout>
    );
};
